/**
 * AppCredentialsManager
 *
 * Manages external API credentials for an application+product combination.
 * Credentials are stored encrypted in the backend and used automatically
 * by the Swagger UI when testing the API.
 *
 * Shown inside the Application card on the product page.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { api, getErrorMessage } from '../lib/api';
import { CheckCircle2, Eye, EyeOff, KeyRound, ShieldCheck, Activity, Trash2, Loader2 } from 'lucide-react';

interface AppCredentialsManagerProps {
  applicationId: string;
  clientId: string;
  productId: string;
  authProvider: string; // e.g. 'BASIC_AUTH', 'OAUTH2', 'API_KEY'
  productName?: string;
}

// Field definitions per provider
const PROVIDER_FIELDS: Record<string, { key: string; label: string; type: 'text' | 'password' }[]> =
  {
    BASIC_AUTH: [
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
    ],
    OAUTH2: [
      { key: 'client_id', label: 'Client ID', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', type: 'password' },
      { key: 'token_url', label: 'Token URL', type: 'text' },
      { key: 'scope', label: 'Scope (opcional)', type: 'text' },
    ],
    API_KEY: [{ key: 'api_key', label: 'API Key', type: 'password' }],
    HMAC: [{ key: 'secret', label: 'Secret Key', type: 'password' }],
    CUSTOM: [{ key: 'value', label: 'Valor', type: 'text' }],
  };

export const AppCredentialsManager: React.FC<AppCredentialsManagerProps> = ({
  applicationId,
  clientId,
  productId,
  authProvider,
  productName,
}) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});

  const credKey = ['app-credentials', applicationId, productId];

  // Fetch existing credentials (masked)
  const { data: credData, isLoading } = useQuery({
    queryKey: credKey,
    queryFn: async () => {
      const r = await api.get<{ success: boolean; configured: boolean; data: any }>(
        `/clients/${clientId}/applications/${applicationId}/credentials/${productId}`
      );
      return r.data;
    },
  });

  const isConfigured = credData?.configured === true;

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, string>) => {
      await api.put(`/clients/${clientId}/applications/${applicationId}/credentials/${productId}`, {
        credentials: values,
        provider: authProvider,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: credKey });
      setShowForm(false);
      setFormValues({});
      Swal.fire({
        title: 'Credenciais guardadas',
        text: 'As credenciais foram guardadas com sucesso e serão usadas automaticamente nos testes.',
        icon: 'success',
        confirmButtonColor: 'var(--color-primary-600)',
        timer: 3000,
        timerProgressBar: true,
      });
    },
    onError: (err) => {
      Swal.fire({ title: 'Erro', text: getErrorMessage(err), icon: 'error' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(
        `/clients/${clientId}/applications/${applicationId}/credentials/${productId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: credKey });
      Swal.fire({
        title: 'Credenciais removidas',
        icon: 'success',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    },
  });

  // Test mutation
  const testMutation = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ success: boolean; message: string }>(
        `/clients/${clientId}/applications/${applicationId}/credentials/${productId}/test`
      );
      return r.data;
    },
    onSuccess: (data) => {
      Swal.fire({
        title: data.success ? 'Credenciais válidas' : 'Credenciais inválidas',
        text: data.message,
        icon: data.success ? 'success' : 'error',
        confirmButtonColor: 'var(--color-primary-600)',
      });
    },
    onError: (err) => {
      Swal.fire({ title: 'Erro ao testar', text: getErrorMessage(err), icon: 'error' });
    },
  });

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Remover credenciais?',
      text: 'As credenciais serão removidas. Terá de as configurar novamente para usar nos testes.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
    });
    if (result.isConfirmed) deleteMutation.mutate();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fields = PROVIDER_FIELDS[authProvider] || PROVIDER_FIELDS.CUSTOM;
    const filled = fields.filter((f) => formValues[f.key]?.trim());
    if (filled.length === 0) return;
    saveMutation.mutate(formValues);
  };

  const fields = PROVIDER_FIELDS[authProvider] || PROVIDER_FIELDS.CUSTOM;
  const providerLabel: Record<string, string> = {
    BASIC_AUTH: 'Basic Auth',
    OAUTH2: 'OAuth 2.0',
    API_KEY: 'API Key',
    HMAC: 'HMAC',
    CUSTOM: 'Custom',
  };

  if (isLoading) return null;

  return (
    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-primary-500" />
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            {productName ? `${productName} — ` : ''}Credenciais{' '}
            {providerLabel[authProvider] || authProvider}
          </span>
          {isConfigured ? (
            <span className="badge-modern badge-success flex items-center gap-1 inline-flex text-xs">
              <CheckCircle2 className="w-3.5 h-3.5" /> Configuradas
            </span>
          ) : (
            <span className="badge-modern badge-secondary text-xs">Não configuradas</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isConfigured && (
            <>
              <button
                type="button"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
                className="btn-base btn-sm btn-secondary flex items-center gap-1.5 text-xs px-2.5 py-1"
                title="Testar conexão com as credenciais salvas"
              >
                {testMutation.isPending ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-500" />
                    <span>A testar...</span>
                  </>
                ) : (
                  <>
                    <Activity className="w-3.5 h-3.5 text-primary-500" />
                    <span>Testar Conexão</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/30"
                title="Remover credenciais"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="btn-base btn-sm btn-primary text-xs px-3 py-1"
          >
            {isConfigured ? 'Actualizar' : 'Configurar'}
          </button>
        </div>
      </div>

      {/* Masked credentials preview */}
      {isConfigured && !showForm && credData?.data?.credentials && (
        <div className="space-y-1.5 mb-2.5">
          {Object.entries(credData.data.credentials as Record<string, string>).map(([key, val]) => (
            <div
              key={key}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800 text-xs"
            >
              <span className="text-slate-500 dark:text-slate-400 w-24 shrink-0 font-medium">{key}</span>
              <code className="text-slate-700 dark:text-slate-300 font-mono flex-1">
                {val}
              </code>
            </div>
          ))}
        </div>
      )}

      {/* Info note */}
      {!showForm && (
        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
          <span>
            {isConfigured
              ? 'Estas credenciais são usadas automaticamente ao testar a API na documentação interativa.'
              : `Configure as credenciais ${providerLabel[authProvider] || ''} para testar a API directamente na documentação.`}
          </span>
        </p>
      )}

      {/* Form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mt-3 space-y-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 animate-fade-in"
        >
          <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 font-medium">
            <ShieldCheck className="w-4 h-4 text-primary-500" />
            <span>As credenciais são encriptadas no backend antes de serem guardadas.</span>
          </div>
          {fields.map((field) => (
            <div key={field.key}>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                {field.label}
              </label>
              <div className="relative">
                <input
                  type={showValues[field.key] ? 'text' : field.type}
                  value={formValues[field.key] || ''}
                  onChange={(e) => setFormValues({ ...formValues, [field.key]: e.target.value })}
                  className="input-modern text-xs pr-10"
                  placeholder={field.type === 'password' ? '••••••••' : ''}
                  autoComplete="off"
                />
                {field.type === 'password' && (
                  <button
                    type="button"
                    onClick={() =>
                      setShowValues({ ...showValues, [field.key]: !showValues[field.key] })
                    }
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    {showValues[field.key] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setFormValues({});
              }}
              className="btn-base btn-secondary btn-sm flex-1 text-xs"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="btn-base btn-primary btn-sm flex-1 text-xs flex items-center justify-center gap-1.5"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>A guardar...</span>
                </>
              ) : (
                'Guardar Credenciais'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
